import type { AppContext } from '@application/context/AppContext';
import { CloudDossierSyncService } from '@application/persistence/CloudDossierSyncService';
import { getBusinessApiClient } from '@infra/api/BusinessApiClient';
import { NetworkStatusService } from '@infra/network/NetworkStatusService';
import { logger } from '@infra/telemetry/Logger';
import { hasHybridConsent } from './HybridConsent';
import { HybridVaultService, readHybridVaultStatus } from './HybridVaultService';

const LIVE_THROTTLE_MS = 15 * 60 * 1000;

export function shouldLiveSyncHybrid(lastLiveSyncAt: string | null | undefined, now = Date.now()): boolean {
  if (!lastLiveSyncAt) return true;
  const then = new Date(lastLiveSyncAt).getTime();
  if (!Number.isFinite(then)) return true;
  return now - then >= LIVE_THROTTLE_MS;
}

/** Sync Hybride au retour réseau : dossier chez Nexxus, sans e-mail à chaque reprise. */
export class HybridLiveSyncService {
  static async syncOnReconnect(
    ctx: AppContext,
    tenantId: string,
  ): Promise<{ synced: boolean; reason?: string }> {
    if (!HybridVaultService.isEnabled(ctx)) return { synced: false, reason: 'not_hybrid' };
    if (!hasHybridConsent(tenantId)) return { synced: false, reason: 'no_consent' };
    if (!NetworkStatusService.isBrowserOnline()) return { synced: false, reason: 'offline' };

    const status = readHybridVaultStatus(tenantId);
    if (!shouldLiveSyncHybrid(status.lastLiveSyncAt)) {
      return { synced: false, reason: 'throttled' };
    }

    try {
      const api = getBusinessApiClient();
      if ((await api.confirmWriteReady()) && api.hasUserSession()) {
        await CloudDossierSyncService.hydrateEntireDossier(tenantId);
      }
    } catch (err) {
      logger.warn({ event: 'hybrid_live_hydrate_skip', error: String(err) });
    }

    const sent = await HybridVaultService.sendNow(ctx, tenantId, { notify: false });
    if (sent.needsSetup) {
      return { synced: false, reason: 'setup_required' };
    }
    if (sent.lastError) {
      logger.warn({ event: 'hybrid_live_sync_failed', error: sent.lastError });
      return { synced: false, reason: sent.lastError };
    }
    logger.info({ event: 'hybrid_live_sync_ok', tenantId });
    return { synced: true };
  }
}
